import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Select, Space, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { usePricingScenarios } from '../../../../graphql/queries/settlement/getSettlementCommonQueries';
import { OPS_TOOL_SETTLEMENT_OPERATION_LOGS } from '../graphql/opsToolSettlementGraphql';

dayjs.extend(utc);

type OperationLogRow = {
  id: string;
  operatorId: string;
  operatorName: string;
  operatedAt: string | null;
  functionType: string;
  changeType: string;
  businessScenarioCode: string | null;
  settlementClientId: string | null;
  operationMode: string | null;
  operationStatus: string;
  affectedCount: number | null;
  skippedCount: number | null;
  errorMessage: string | null;
  operationDetail: string | null;
};

const FUNCTION_TYPE_OPTIONS = [
  { value: 'BILLING_PERIOD_FEE_ITEM', label: '账期费用项配置' },
  { value: 'BILL_FEE_SUMMARY', label: '账单费用项汇总' },
  { value: 'TMP_RUN_TASK_RERUN', label: 'tmp_run_task重跑' },
];

const CHANGE_TYPE_OPTIONS = [
  { value: 'CREATE_FEE_ITEM', label: '创建费用项' },
  { value: 'UPDATE_PERIOD_FEE_ITEMS', label: '更新账期费用项' },
  { value: 'ADD_BILL_FEE_SUMMARY', label: '新增账单费用汇总' },
  { value: 'CLEAR_TMP_RUN_TASK', label: '清空tmp_run_task' },
  { value: 'INSERT_TMP_RUN_TASK', label: '插入tmp_run_task' },
  { value: 'RERUN_NEW_ZONE_BY_TMP_RUN_TASK', label: '重跑newZone' },
  { value: 'RERUN_PRICING_BY_TMP_RUN_TASK', label: '重跑计费（收入侧尾程段 / DSP侧）' },
  { value: 'RERUN_TT_SORTING_PICKUP_UNLOADING_FEE_BY_TMP_RUN_TASK', label: '重跑计费（收入侧分拣段）' },
  { value: 'RESET_TMP_RUN_TASK_CONFIG_FOR_CLEARING', label: '重置清分配置' },
  { value: 'RERUN_VOUCHER_CLEARING_BY_TMP_RUN_TASK', label: '重跑清分' },
];

const FUNCTION_TYPE_LABEL: Record<string, string> = Object.fromEntries(
  FUNCTION_TYPE_OPTIONS.map(o => [o.value, o.label])
);
const CHANGE_TYPE_LABEL: Record<string, string> = Object.fromEntries(
  CHANGE_TYPE_OPTIONS.map(o => [o.value, o.label])
);

const formatJsonDetail = (raw: string | null): string => {
  if (!raw) return '-';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

const OperationLogTab: React.FC = () => {
  const { scenarios, loading: scenariosLoading } = usePricingScenarios();
  const [functionType, setFunctionType] = useState<string | undefined>();
  const [changeType, setChangeType] = useState<string | undefined>();
  const [businessScenarioCode, setBusinessScenarioCode] = useState<number | undefined>();
  const [operatedRange, setOperatedRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const scenarioOptions = useMemo(
    () => scenarios.map(s => ({ value: Number(s.businessScenarioCode), label: s.businessScenarioName })),
    [scenarios]
  );

  const [loadLogs, { data, loading, refetch }] = useLazyQuery(OPS_TOOL_SETTLEMENT_OPERATION_LOGS);

  const buildFilters = useCallback(() => {
    const operatedAtFrom = operatedRange?.[0]
      ? dayjs(operatedRange[0]).startOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
      : undefined;
    const operatedAtTo = operatedRange?.[1]
      ? dayjs(operatedRange[1]).endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
      : undefined;
    return {
      functionType: functionType ?? null,
      changeType: changeType ?? null,
      businessScenarioCode: businessScenarioCode ?? null,
      operatedAtFrom,
      operatedAtTo,
    };
  }, [functionType, changeType, businessScenarioCode, operatedRange]);

  const fetchLogs = useCallback(
    (targetPage = page, targetSize = pageSize) => {
      loadLogs({
        variables: {
          filters: buildFilters(),
          pageRequest: { page: targetPage, size: targetSize },
        },
      });
    },
    [loadLogs, buildFilters, page, pageSize]
  );

  useEffect(() => {
    fetchLogs(1, pageSize);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [functionType, changeType, businessScenarioCode, operatedRange]);

  const pageResult = data?.opsToolSettlementOperationLogs;
  const rows: OperationLogRow[] = useMemo(
    () =>
      (pageResult?.content ?? []).map((row: OperationLogRow) => ({
        ...row,
        id: String(row.id),
        operatorId: String(row.operatorId),
        businessScenarioCode: row.businessScenarioCode != null ? String(row.businessScenarioCode) : null,
        settlementClientId: row.settlementClientId != null ? String(row.settlementClientId) : null,
      })),
    [pageResult]
  );

  const columns: ColumnsType<OperationLogRow> = [
    { title: '操作时间(UTC)', dataIndex: 'operatedAt', width: 180 },
    { title: '操作人', dataIndex: 'operatorName', width: 120 },
    { title: '操作人ID', dataIndex: 'operatorId', width: 100 },
    {
      title: '功能类型',
      dataIndex: 'functionType',
      width: 140,
      render: (v: string) => FUNCTION_TYPE_LABEL[v] ?? v,
    },
    {
      title: '变更类型',
      dataIndex: 'changeType',
      width: 140,
      render: (v: string) => CHANGE_TYPE_LABEL[v] ?? v,
    },
    {
      title: '业务场景',
      dataIndex: 'businessScenarioCode',
      width: 120,
      render: (v: string | null) => {
        if (!v) return '-';
        const matched = scenarioOptions.find(s => String(s.value) === v);
        return matched ? `${matched.label}(${v})` : v;
      },
    },
    {
      title: '操作范围',
      dataIndex: 'operationMode',
      width: 90,
      render: (v: string | null) => v ?? '-',
    },
    {
      title: '结果',
      dataIndex: 'operationStatus',
      width: 90,
      render: (v: string) =>
        v === 'SUCCESS' ? <Tag color="success">成功</Tag> : <Tag color="error">失败</Tag>,
    },
    { title: '影响条数', dataIndex: 'affectedCount', width: 90 },
    { title: '跳过条数', dataIndex: 'skippedCount', width: 90 },
    {
      title: '失败原因',
      dataIndex: 'errorMessage',
      ellipsis: true,
      render: (v: string | null) => v ?? '-',
    },
  ];

  const handleSearch = () => {
    setPage(1);
    fetchLogs(1, pageSize);
  };

  useEffect(() => {
    if (pageResult && !pageResult.success) {
      message.error(pageResult.message || '查询操作记录失败');
    }
  }, [pageResult]);

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Space wrap>
        <Select
          allowClear
          placeholder="功能类型"
          style={{ width: 180 }}
          options={FUNCTION_TYPE_OPTIONS}
          value={functionType}
          onChange={setFunctionType}
        />
        <Select
          allowClear
          placeholder="变更类型"
          style={{ width: 180 }}
          options={CHANGE_TYPE_OPTIONS}
          value={changeType}
          onChange={setChangeType}
        />
        <Select
          allowClear
          placeholder="业务场景"
          style={{ width: 220 }}
          loading={scenariosLoading}
          options={scenarioOptions}
          value={businessScenarioCode}
          onChange={setBusinessScenarioCode}
        />
        <DatePicker.RangePicker
          value={operatedRange}
          onChange={range => setOperatedRange(range)}
          placeholder={['操作开始(UTC)', '操作结束(UTC)']}
        />
        <Button type="primary" onClick={handleSearch}>
          查询
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => refetch?.()}>
          刷新
        </Button>
      </Space>

      <Table<OperationLogRow>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 1400 }}
        expandable={{
          expandedRowRender: record => (
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 480, overflow: 'auto' }}>
              {formatJsonDetail(record.operationDetail)}
            </pre>
          ),
          rowExpandable: record => Boolean(record.operationDetail),
        }}
        pagination={{
          current: page,
          pageSize,
          total: pageResult?.totalElements ?? 0,
          showSizeChanger: true,
          showTotal: total => `共 ${total} 条`,
          onChange: (p, size) => {
            setPage(p);
            setPageSize(size);
            fetchLogs(p, size);
          },
        }}
      />
    </Space>
  );
};

export default OperationLogTab;
